/* Add/verify/manage multiple custom domains for a Nextcloud cloud.
 *
 * Wires up the new /api/admin/clouds/{id}/domains + /api/admin/domains/{id}/verify
 * endpoints. Embedded on CloudDetailPage. Shows the operator/reseller
 * exactly which DNS-target to point their CNAME/A record at, then a
 * "Verify DNS" button runs the live resolver check and pushes the Caddy
 * route on success (auto-SSL via Let's-Encrypt).
 */
import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Globe2, ShieldCheck, AlertTriangle, RefreshCcw, Trash2, Star } from "lucide-react";

export default function CloudDomainsPanel({ cloudId, cloud, domains = [], onChange }) {
    const [hostname, setHostname] = useState("");
    const [isPrimary, setIsPrimary] = useState(false);
    const [busy, setBusy] = useState(null); // null | "add" | "verify-<id>" | "delete-<id>" | "primary-<id>"
    const [err, setErr] = useState(null);
    const [lastAdded, setLastAdded] = useState(null);
    // Optimistic local-state shadow: a list of domains added/removed since
    // the last successful SWR refetch. Merged with the `domains` prop on
    // render so the user sees their change instantly instead of waiting
    // up to ~9s for SWR's dedupe + refetch cycle.
    const [optimistic, setOptimistic] = useState({ added: [], removedIds: [] });

    // Merge: parent list takes priority once it includes the optimistic id.
    const mergedDomains = React.useMemo(() => {
        const fromServer = domains.filter((d) => !optimistic.removedIds.includes(d.id));
        const serverIds = new Set(fromServer.map((d) => d.id));
        const stillPending = optimistic.added.filter((d) => !serverIds.has(d.id));
        return [...fromServer, ...stillPending];
    }, [domains, optimistic]);

    const addDomain = async () => {
        const h = hostname.trim().toLowerCase();
        if (!h) return;
        setBusy("add");
        setErr(null);
        try {
            const { data } = await api.post(
                `/api/admin/clouds/${cloudId}/domains`,
                { hostname: h, is_primary: isPrimary },
            );
            setLastAdded(data);
            setOptimistic((o) => ({ ...o, added: [...o.added, data] }));
            setHostname("");
            setIsPrimary(false);
            await onChange?.();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(null);
    };

    const verify = async (id) => {
        setBusy(`verify-${id}`);
        setErr(null);
        try {
            await api.post(`/api/admin/domains/${id}/verify`);
            await onChange?.();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(null);
    };

    const setPrimary = async (id) => {
        setBusy(`primary-${id}`);
        setErr(null);
        try {
            await api.post(`/api/admin/domains/${id}/set-primary`);
            await onChange?.();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(null);
    };

    const del = async (id, host) => {
        if (!window.confirm(`Delete domain ${host}? This removes the route from every LB.`)) return;
        setBusy(`delete-${id}`);
        setErr(null);
        try {
            await api.delete(`/api/admin/domains/${id}`);
            setOptimistic((o) => ({ ...o, removedIds: [...o.removedIds, id] }));
            await onChange?.();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(null);
    };

    return (
        <div data-testid="cloud-domains-panel">
            <div className="cm-label mb-2 flex items-center gap-1.5">
                <Globe2 size={11} /> Domains
                <span className="text-zinc-600">·</span>
                <span className="text-zinc-500">{mergedDomains.length}</span>
            </div>

            {/* Existing domains list */}
            {/* Notfall-Domain — read-only Anzeige: lebt als cloud.fallback_hostname
                (wildcard-DNS + wildcard-TLS auf den LBs), NICHT in der domains-
                Collection. Ohne diese Karte ist sie für den Operator unsichtbar —
                "bekommt die Cloud überhaupt eine .connect.oncloud.zone Domain?" */}
            {cloud?.fallback_hostname && (
                <div
                    data-testid="domain-fallback"
                    className="bg-zinc-950 border border-sky-900 p-2 text-xs mb-2"
                >
                    <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                            <div className="font-mono text-zinc-200 flex items-center gap-1 truncate">
                                <ShieldCheck size={11} className="flex-shrink-0 text-sky-400" />
                                {cloud.fallback_hostname}
                                <span
                                    data-testid="domain-fallback-badge"
                                    className="cm-badge cm-badge-neutral text-[10px] ml-1"
                                >
                                    FALLBACK
                                </span>
                            </div>
                            <div className="font-mono text-[11px] text-zinc-500 mt-0.5">
                                DNS: <span className="text-emerald-500">wildcard ✓</span>
                                {" "}·{" "}
                                SSL: <span className="text-emerald-500">wildcard ✓</span>
                                {" "}·{" "}
                                <span className="text-zinc-600">always reachable (LB wildcard route)</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {mergedDomains.map((d) => {
                const target = d.lb_dns_target || d.expected_dns_target;
                const verifying = busy === `verify-${d.id}`;
                const deleting = busy === `delete-${d.id}`;
                const promoting = busy === `primary-${d.id}`;
                return (
                    <div
                        key={d.id}
                        className="bg-zinc-950 border border-zinc-800 p-2 text-xs mb-2"
                        data-testid={`domain-${d.id}`}
                    >
                        <div className="flex justify-between items-start gap-2">
                            <div className="flex-1 min-w-0">
                                <div className="font-mono text-zinc-200 flex items-center gap-1 truncate">
                                    <Globe2 size={11} className="flex-shrink-0" />
                                    {d.hostname}
                                    {d.is_primary && (
                                        <span
                                            data-testid={`domain-${d.id}-primary-badge`}
                                            className="cm-badge cm-badge-success text-[10px] ml-1"
                                        >
                                            primary
                                        </span>
                                    )}
                                </div>
                                <div className="font-mono text-[11px] text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                                    <span className="flex items-center gap-0.5">
                                        DNS:{" "}
                                        {d.dns_validated ? (
                                            <span className="text-emerald-500 flex items-center gap-0.5">
                                                <ShieldCheck size={9} /> validated
                                            </span>
                                        ) : (
                                            <span className="text-amber-500 flex items-center gap-0.5">
                                                <AlertTriangle size={9} /> pending
                                            </span>
                                        )}
                                    </span>
                                    <span>·</span>
                                    <span>SSL: {d.ssl_status}</span>
                                </div>
                                {target?.dns_target && (
                                    <div className="font-mono text-[11px] text-zinc-600 mt-0.5">
                                        {target.dns_target_kind || "CNAME"} → {target.dns_target}
                                    </div>
                                )}
                                {d.last_verification_error && !d.dns_validated && (
                                    <div className="font-mono text-[11px] text-rose-400 mt-1 break-words">
                                        {d.last_verification_error}
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col gap-1 flex-shrink-0">
                                {!d.dns_validated && (
                                    <button
                                        data-testid={`domain-${d.id}-verify-button`}
                                        className="cm-btn-secondary text-[11px] flex items-center gap-1 px-2 py-0.5"
                                        disabled={!!busy}
                                        onClick={() => verify(d.id)}
                                    >
                                        <RefreshCcw size={9} /> {verifying ? "..." : "Verify"}
                                    </button>
                                )}
                                {d.dns_validated && !d.is_primary && (
                                    <button
                                        data-testid={`domain-${d.id}-primary-button`}
                                        className="cm-btn-secondary text-[11px] flex items-center gap-1 px-2 py-0.5"
                                        disabled={!!busy}
                                        onClick={() => setPrimary(d.id)}
                                    >
                                        <Star size={9} /> {promoting ? "..." : "Primary"}
                                    </button>
                                )}
                                <button
                                    data-testid={`domain-${d.id}-delete-button`}
                                    className="cm-btn-secondary text-[11px] flex items-center gap-1 px-2 py-0.5 text-rose-400 hover:bg-rose-950/30"
                                    disabled={!!busy}
                                    onClick={() => del(d.id, d.hostname)}
                                >
                                    <Trash2 size={9} /> {deleting ? "..." : "Delete"}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })}

            {/* Last-added instructions panel */}
            {lastAdded && !lastAdded.dns_validated && (
                <div
                    data-testid="cloud-domain-add-instructions"
                    className="bg-emerald-950/30 border border-emerald-800 p-2 text-xs mb-2 font-mono"
                >
                    <div className="text-emerald-400 mb-1">
                        ✓ Domain {lastAdded.hostname} created — DNS not yet validated.
                    </div>
                    <div className="text-[11px] text-zinc-300">
                        {lastAdded.instructions}
                    </div>
                </div>
            )}

            {/* Add form */}
            <div
                data-testid="cloud-domain-add-form"
                className="mt-3 bg-zinc-950 border border-zinc-800 p-2"
            >
                <div className="text-[11px] text-zinc-400 mb-1 font-mono">
                    Add custom domain
                </div>
                <div className="flex gap-1.5 mb-1.5">
                    <input
                        data-testid="cloud-domain-add-hostname-input"
                        type="text"
                        placeholder="cloud.example.de"
                        value={hostname}
                        onChange={(e) => setHostname(e.target.value)}
                        className="flex-1 cm-input text-xs px-2 py-1 font-mono"
                        disabled={busy === "add"}
                    />
                    <button
                        data-testid="cloud-domain-add-submit-button"
                        className="cm-btn-primary text-xs px-3 py-1"
                        disabled={busy === "add" || !hostname.trim()}
                        onClick={addDomain}
                    >
                        {busy === "add" ? "..." : "Add"}
                    </button>
                </div>
                <label className="flex items-center gap-1 text-[11px] text-zinc-400 font-mono">
                    <input
                        data-testid="cloud-domain-add-primary-checkbox"
                        type="checkbox"
                        checked={isPrimary}
                        onChange={(e) => setIsPrimary(e.target.checked)}
                        disabled={busy === "add"}
                    />
                    set as primary (overrides current primary_domain)
                </label>
            </div>

            {err && (
                <div
                    data-testid="cloud-domain-error"
                    className="mt-2 bg-rose-950/30 border border-rose-800 text-rose-300 text-xs p-2 font-mono"
                >
                    {err}
                </div>
            )}
        </div>
    );
}
